import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import GoogleMap from 'react-google-map';
import { withRouter } from 'react-router';

import lodashDebounce from 'lodash/debounce';
import lodashUniq from 'lodash/uniq';

import withGoogleMaps from 'client/helpers/withGoogleMaps';
import {
  getCurrentLocation,
  getMapArea,
  getMarkerShape,
  getMarkerIconConfig,
  getMarkerLabelConfig,
  centerMarkersOnMap,
  fitMarkerOnMap,
  GOOGLE_MAPS_MARKER_THEME_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING,
  GOOGLE_MAPS_MARKER_SHAPE_BOX,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED,
} from 'client/helpers/googleMapsHelpers';
import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_LOCKBOX_TYPE_LOCKBOX,
} from 'common/listingConstants';

import branch from 'client/core/branch';
import AgentActions from 'client/actions/Agent';

const getMarkerConfig = ({
  map,
  googleMaps,
  listing,
  tour,
  viewedListingIds,
  viewingListing = {},
  currentUser,
}) => {
  const listingIds = tour.listings.map(({ id }) => id);
  const bookmarkIds = (tour.bookmarks || [])
    .filter((bookmark) => bookmark)
    .map(({ listingId }) => listingId);

  const markerTheme =
    viewingListing.id === listing.id
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING
      : listingIds.includes(listing.id)
      ? GOOGLE_MAPS_MARKER_THEME_TOUR
      : bookmarkIds.includes(listing.id)
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED
      : viewedListingIds.includes(listing.id)
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED
      : GOOGLE_MAPS_MARKER_THEME_LISTING;

  const markerShape =
    listingIds.includes(listing.id) || bookmarkIds.includes(listing.id)
      ? GOOGLE_MAPS_MARKER_SHAPE_BOX
      : getMarkerShape(map.getZoom());

  return {
    icon: getMarkerIconConfig({
      markerShape,
      googleMaps,
      markerTheme: markerTheme,
    }),
    label: getMarkerLabelConfig({
      markerShape,
      listing,
      currentUser,
    }),
    zIndex: listingIds.includes(listing.id) ? 10 : 1,
    optimized: false,
  };
};

@withRouter
@withGoogleMaps
@branch({
  tours: ['agent', 'tours'],
  mapZoomLevel: ['agentMapView', 'mapZoomLevel'],
  mapCenter: ['agentMapView', 'mapCenter'],
  mapChanged: ['agentMapView', 'mapChanged'],
  viewingTour: ['agentMapView', 'viewingTour'],
  viewedListingIds: ['agentMapView', 'viewedListingIds'],
  listings: ['agentMapView', 'listings'],
  availableHomesToTourNow: ['agentMapView', 'availableHomesToTourNow'],
})
class SrAgentMapView extends Component {
  static propTypes = {
    tours: PropTypes.array,
    mapZoomLevel: PropTypes.number.isRequired,
    mapCenter: PropTypes.object.isRequired,
    mapChanged: PropTypes.bool.isRequired,
    viewingTour: PropTypes.object,
    viewedListingIds: PropTypes.array.isRequired,
    listings: PropTypes.array.isRequired,
    availableHomesToTourNow: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    googleMaps: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
  };

  constructor() {
    super();

    this.state = {
      map: null,
      markers: new Map(),
    };
  }

  componentDidUpdate() {
    const { markers } = this.state;
    const { viewingTour, availableHomesToTourNow } = this.props;
    const listingsIdsInTour = viewingTour ? viewingTour.listings.map(({ id }) => id) : [];

    const listings = this.props.listings.filter((listing) => {
      if (listingsIdsInTour.includes(listing.id)) {
        return true;
      }

      if (!availableHomesToTourNow) {
        return true;
      }

      return (
        listing.occupantType === LISTING_OCCUPANT_TYPE_VACANT &&
        listing.lockboxType === LISTING_LOCKBOX_TYPE_LOCKBOX
      );
    });

    const listingIds = listings.map((listing) => listing.id);
    const listingsToAdd = listings.filter((listing) => !markers.has(listing.id));
    const listingIdsToRemove = Array.from(markers.keys()).filter(
      (listingId) => !listingIds.includes(listingId),
    );

    if (listingIdsToRemove.length) {
      this._removeMarkersById(listingIdsToRemove);
    }

    if (listingsToAdd.length) {
      this._addMarkers(listingsToAdd);
    }
  }

  componentWillUnmount() {
    this._removeAllMarkers();
  }

  _getUserPosition = () => {
    const { map } = this.state;
    const { googleMaps } = this.props;

    getCurrentLocation().then(({ coords }) => {
      if (googleMaps && map && coords) {
        this.userLocationCircle = new googleMaps.Marker({
          map: map,
          position: {
            lat: coords.latitude,
            lng: coords.longitude,
          },
          icon: {
            path: googleMaps.SymbolPath.CIRCLE,
            fillColor: '#C8D6EC',
            fillOpacity: 0.8,
            scale: 13,
            strokeWeight: 0,
          },
        });

        this.userLocationMarker = new googleMaps.Marker({
          map: map,
          position: {
            lat: coords.latitude,
            lng: coords.longitude,
          },
          clickable: false,
          cursor: 'pointer',
          draggable: false,
          flat: true,
          icon: {
            path: googleMaps.SymbolPath.CIRCLE,
            fillColor: '#4285F4',
            fillOpacity: 1,
            scale: 6,
            strokeColor: 'white',
            strokeWeight: 2,
          },
          optimized: false,
        });
      }
    });
  };

  _onLoadedMap = (googleMaps, map) => {
    const { viewingTour, listings, mapChanged } = this.props;

    this.setState({ map }, () => {
      if (viewingTour) {
        this._addMarkers(listings, { shouldCenterMarkers: !mapChanged });
      }

      this._getUserPosition();
    });

    map.addListener('zoom_changed', this._onZoomChange);
    map.addListener('bounds_changed', this._onBoundsChange);
    map.addListener('center_changed', this._onCenterChange);
    map.addListener('click', this._onClickMap);
  };

  _onZoomChange = () => {
    const { map, markers } = this.state;
    const {
      dispatch,
      googleMaps,
      mapZoomLevel,
      viewingTour,
      viewingListing,
      viewedListingIds,
      listings,
    } = this.props;
    const newZoomLevel = map.getZoom();
    const newMarkerShape = getMarkerShape(newZoomLevel);

    dispatch(AgentActions.setAgentMapViewValue, 'mapZoomLevel', map.getZoom());

    if (newMarkerShape !== getMarkerShape(mapZoomLevel)) {
      listings.forEach((listing) => {
        if (markers.has(listing.id)) {
          const marker = markers.get(listing.id);
          marker.setOptions(
            getMarkerConfig({
              map,
              googleMaps,
              listing,
              viewingListing,
              viewedListingIds,
              tour: viewingTour,
              currentUser: viewingTour.users[0],
            }),
          );
        }
      });
    }
  };

  _onCenterChange = lodashDebounce(() => {
    const mapCenter = this.state.map.getCenter();
    this.props.dispatch(AgentActions.setAgentMapCenter, {
      lat: mapCenter.lat(),
      lng: mapCenter.lng(),
    });
  }, 400);

  _onBoundsChange = lodashDebounce(() => {
    const { map } = this.state;
    const { dispatch, viewingTour } = this.props;
    const mapArea = getMapArea({ map });

    dispatch(AgentActions.setAgentMapViewValue, 'mapArea', mapArea);

    if (viewingTour) {
      this._getListings(mapArea);
    }
  }, 1000);

  _onSelectTour = (tourId) => {
    this.props.dispatch(AgentActions.setViewingTour, tourId);
  };

  _getListings = () => {
    this.props.dispatch(AgentActions.getListingsForViewingTour);
  };

  _addMarkers = (listingsToAdd, opts = {}) => {
    const { map, markers } = this.state;
    const { googleMaps, viewingTour } = this.props;

    if (!googleMaps) {
      return;
    }

    listingsToAdd.forEach((listing) => {
      if (!markers.has(listing.id)) {
        markers.set(listing.id, this._addMarker({ listing, tour: viewingTour }));
      }
    });
    this.setState({ markers });

    if (opts.shouldCenterMarkers) {
      centerMarkersOnMap({ googleMaps, map, markers });
    }
  };

  _addMarker = ({ listing, tour }) => {
    const { map } = this.state;
    const { googleMaps, viewedListingIds } = this.props;
    const marketConfig = getMarkerConfig({
      map,
      googleMaps,
      listing,
      tour,
      viewedListingIds,
      currentUser: tour.users[0],
    });

    const marker = new googleMaps.Marker({
      map: map,
      position: {
        lat: listing.locationLatitude,
        lng: listing.locationLongitude,
      },
      ...marketConfig,
    });

    googleMaps.event.addListener(marker, 'click', () => {
      this._onClickMarker(listing);
    });

    return marker;
  };

  _removeMarkersById = (listingIds) => {
    const { markers } = this.state;
    listingIds.forEach((listingId) => {
      const marker = markers.get(listingId);
      if (marker) {
        marker.setMap(null);
      }
      markers.delete(listingId);
    });

    this.setState({ markers });
  };

  _removeAllMarkers = () => {
    const { markers } = this.state;
    this._removeMarkersById(Array.from(markers.keys()));
  };

  _onClickMap = () => {
    this.props.dispatch(AgentActions.setAgentMapViewValue, 'viewingListing', null);
  };

  _onClickMarker = (listing) => {
    const { dispatch, googleMaps, viewedListingIds, viewingTour, listings, isMobile } = this.props;
    const { map, markers } = this.state;
    const viewingListing = listing;

    viewedListingIds.forEach((viewedListingId) => {
      const viewedMarker = markers.get(viewedListingId);
      if (viewedMarker && viewedListingId !== listing.id) {
        const listing = listings.find((listing) => listing.id === viewedListingId);
        if (!listing) {
          return;
        }

        viewedMarker.setOptions({
          ...getMarkerConfig({
            map,
            googleMaps,
            viewedListingIds,
            viewingListing,
            listing: listing,
            tour: viewingTour,
            currentUser: viewingTour.users[0],
          }),
        });
      }
    });

    const marker = markers.get(listing.id);
    marker.setOptions(
      getMarkerConfig({
        map,
        googleMaps,
        listing,
        viewedListingIds,
        viewingListing,
        tour: viewingTour,
        currentUser: viewingTour.users[0],
      }),
    );

    fitMarkerOnMap({ marker, googleMaps, isMobile, map });

    dispatch(AgentActions.setAgentMapViewValue, 'viewingListing', listing);
    dispatch(
      AgentActions.setAgentMapViewValue,
      'viewedListingIds',
      lodashUniq([...viewedListingIds, listing.id]),
    );
  };

  _toggleOnDemandListings = () => {
    const { dispatch, availableHomesToTourNow } = this.props;
    dispatch(
      AgentActions.setAgentMapViewValue,
      'availableHomesToTourNow',
      !availableHomesToTourNow,
    );
    this._getListings();
  };

  render() {
    const {
      googleMaps,
      mapZoomLevel,
      mapCenter,
      tours,
      viewingTour,
      availableHomesToTourNow,
    } = this.props;
    return (
      <div className="mapContainer">
        {tours && tours.length && (
          <div className="searchDropdown">
            <span className="title">
              search as: {viewingTour ? viewingTour.users[0].name.toLowerCase() : ''}
            </span>
            <div className="dropdown">
              {tours.map((tour) => (
                <div className="option" key={tour.id} onClick={() => this._onSelectTour(tour.id)}>
                  {tour.users[0].name.toLowerCase()}
                </div>
              ))}
            </div>
          </div>
        )}

        {tours && tours.length && (
          <div
            className={classNames('filterSwitch', { active: availableHomesToTourNow })}
            onClick={this._toggleOnDemandListings}
          >
            <span className="label">on-demand</span>
          </div>
        )}

        <div className="innerMapContainer">
          <GoogleMap
            googleMaps={googleMaps}
            center={mapCenter}
            zoom={mapZoomLevel}
            gestureHandling="greedy"
            clickableIcons={false}
            zoomControlOptions={{
              position: googleMaps.ControlPosition.RIGHT_TOP,
            }}
            disableDefaultUI
            onLoaded={this._onLoadedMap}
          />
        </div>
      </div>
    );
  }
}

SrAgentMapView.displayName = 'SrAgentMapView';

export default SrAgentMapView;
